import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queueApi } from "@bw/shared/api";
import { queryKeys } from "../../../common/query/queryClient";

/** The party object returned by POST /queue/party (leader = token user). */
type PartyResponse = Awaited<ReturnType<typeof queueApi.createParty>>;

/**
 * Create a party at a facility (POST /queue/party).
 *
 * The leader is the token user (server-derived); the only input is the facility,
 * captured at hook construction. On success the caller navigates to the party
 * detail page with the returned party_id. Errors are NOT swallowed.
 */
export function useCreateParty(facilityId: string) {
  const queryClient = useQueryClient();
  return useMutation<PartyResponse, unknown, void>({
    mutationFn: () => queueApi.createParty(facilityId),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.session(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.facility(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: ["dropin", "my-entry", facilityId],
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.myParty(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.partyMemberships(facilityId),
        refetchType: "active",
      });
    },
  });
}
